import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertTriangle, XCircle, Clock, QrCode, Phone, X as CloseIcon, Lock, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

export default function PublicScan() {
  const { code } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  // PIN gate state — populated when backend returns requires_pin:true
  const [pinRequired, setPinRequired] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [pinError, setPinError] = useState(null);

  useEffect(() => {
    // withCredentials so the signed 10-min smpin_* cookie travels back
    // to the API — enforcement officers scanning multiple vehicles on
    // the same site don't retype the PIN each time.
    axios
      .get(`${API}/scan/${code}`, { withCredentials: true })
      .then((r) => {
        if (r.data?.requires_pin) {
          setPinRequired(true);
        } else {
          setResult(r.data);
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [code]);

  const submitPin = async (e) => {
    e?.preventDefault?.();
    if (!pinValue || pinSubmitting) return;
    setPinSubmitting(true);
    setPinError(null);
    try {
      const r = await axios.post(
        `${API}/scan/${code}/verify-pin`,
        { pin: pinValue },
        { withCredentials: true },
      );
      setResult(r.data);
      setPinRequired(false);
      setPinValue("");
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Verification failed");
    } finally {
      setPinSubmitting(false);
    }
  };

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (error)
    return (
      <ScanShell color="#EF4444">
        <div className="text-center">
          <XCircle className="w-14 h-14 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display text-3xl font-bold">QR NOT FOUND</div>
          <div className="text-white/70 mt-2">This QR is invalid or has been removed.</div>
        </div>
      </ScanShell>
    );

  if (pinRequired)
    return (
      <ScanShell color="#0F8F4F">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <Lock className="w-14 h-14 mx-auto mb-4" strokeWidth={1.5} />
          <div className="mono-tiny text-white/70">Private verification</div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            Enter property PIN
          </div>
          <div className="text-white/70 text-sm mt-2">
            Enforcement, security, or towing: enter the PIN provided by
            property management to view verification details.
          </div>
        </motion.div>
        <form
          onSubmit={submitPin}
          className="bg-white text-[#09090B] rounded-xl p-6 md:p-7 mt-8 shadow-xl space-y-4"
          data-testid="scan-pin-form"
        >
          <label className="mono-tiny text-[#71717A] block">
            Property PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            maxLength={10}
            value={pinValue}
            onChange={(e) => {
              setPinError(null);
              setPinValue(e.target.value.replace(/\D/g, ""));
            }}
            placeholder="••••"
            className="w-full h-14 border border-[#E4E4E7] rounded-md px-4 text-center text-2xl tracking-widest font-mono focus:outline-none focus:border-[#0F8F4F] focus:ring-2 focus:ring-[#0F8F4F]/20"
            data-testid="scan-pin-input"
          />
          {pinError && (
            <div
              className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#EF4444]/30 rounded-md px-3 py-2"
              data-testid="scan-pin-error"
            >
              {pinError}
            </div>
          )}
          <button
            type="submit"
            disabled={!pinValue || pinSubmitting}
            className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] disabled:opacity-50 text-white font-semibold rounded-md flex items-center justify-center gap-2 transition-colors"
            data-testid="scan-pin-submit"
          >
            {pinSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" strokeWidth={2} />
                Unlock verification
              </>
            )}
          </button>
          <div className="mono-tiny text-[#71717A] text-center">
            One correct PIN unlocks this device for 10 minutes.
          </div>
        </form>
        <div className="text-center text-white/50 mt-8 mono-tiny">
          Verified via ScanMint · Private mode
        </div>
      </ScanShell>
    );

  if (!result)
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-sm text-[#71717A]">Verifying…</div>
      </div>
    );

  const statusMeta = {
    active: { color: "#00C16E", icon: CheckCircle2, label: "VERIFIED · ACTIVE" },
    suspended: result.payment_suspended
      ? { color: "#EF4444", icon: AlertTriangle, label: "SUSPENDED · PAYMENT REQUIRED" }
      : { color: "#EF4444", icon: AlertTriangle, label: "SUSPENDED" },
    expired: { color: "#FACC15", icon: Clock, label: "EXPIRED" },
    inactive: { color: "#71717A", icon: Clock, label: "INACTIVE" },
    pending_invoice: { color: "#FACC15", icon: Clock, label: "PENDING ACTIVATION" },
    removed: { color: "#EF4444", icon: XCircle, label: "REMOVED" },
  }[result.status] || { color: "#71717A", icon: Clock, label: result.status?.toUpperCase() };

  const Icon = statusMeta.icon;
  const v = result.vehicle;

  return (
    <ScanShell color={statusMeta.color}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <Icon className="w-16 h-16 mx-auto mb-4" strokeWidth={1.5} />
        <div className="mono-tiny text-white/70">Scan result</div>
        <div className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2" data-testid="scan-status">
          {statusMeta.label}
        </div>
      </motion.div>

      <div className="bg-white text-[#09090B] rounded-xl p-6 md:p-8 mt-8 shadow-xl">
        {result.payment_suspended && (
          <div
            className="mb-5 rounded-md border border-[#EF4444]/30 bg-[#FEE2E2] p-4 text-sm text-[#7F1D1D] flex items-start gap-2"
            data-testid="scan-suspended-banner"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">Suspended — payment required.</div>
              This QR is temporarily inactive while the property settles
              its monthly invoice. Please contact property management.
            </div>
          </div>
        )}
        {!result.vehicle && !result.payment_suspended && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-lg border-2 border-[#F59E0B]/40 bg-gradient-to-b from-[#FFFBEA] to-white p-8 md:p-10 text-center"
            data-testid="scan-pre-enrollment-banner"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-[#FACC15]/20 flex items-center justify-center">
              <Clock className="w-8 h-8 text-[#92400E]" strokeWidth={1.5} />
            </div>
            <div className="font-display text-2xl md:text-3xl font-bold text-[#09090B] mt-5">
              Complete enrollment to activate QR verification
            </div>
            <div className="text-sm md:text-base text-[#92400E] mt-3 max-w-md mx-auto leading-relaxed">
              This QR isn't active yet. Once the property completes
              enrollment and the invoice is paid, full verification
              details will appear here automatically.
            </div>
          </motion.div>
        )}
        {result.vehicle && (
          <motion.div
            key="active-block"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
        <div className="mono-tiny text-[#71717A]">Property</div>
        <div className="font-display text-xl font-bold mt-1" data-testid="scan-property">
          {result.property_name}
        </div>
        <div className="text-sm text-[#71717A]">{result.corporation_name}</div>
        <div className="text-sm text-[#71717A]">{result.address}</div>

        {result.tenant && (result.tenant.full_name || result.tenant.unit_number) && (
          <>
            <div className="my-5 h-px bg-[#E4E4E7]" />
            <div className="mono-tiny text-[#71717A]">Resident</div>
            {result.tenant.full_name && (
              <div className="font-display text-lg font-bold mt-1" data-testid="scan-tenant-name">
                {result.tenant.full_name}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              {result.tenant.building && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded bg-[#F4F4F5] text-[#3F3F46] font-medium"
                  data-testid="scan-tenant-building"
                >
                  Building {result.tenant.building}
                </span>
              )}
              {result.tenant.unit_number && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded bg-[#0F8F4F]/10 text-[#0F8F4F] font-semibold"
                  data-testid="scan-tenant-unit"
                >
                  Unit {result.tenant.unit_number}
                </span>
              )}
            </div>
            {result.tenant.phone && (
              <a
                href={`tel:${result.tenant.phone}`}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#0F8F4F] hover:text-[#0A6B3B] hover:underline"
                data-testid="scan-tenant-phone"
              >
                <Phone className="w-4 h-4" strokeWidth={2} />
                {result.tenant.phone}
              </a>
            )}
          </>
        )}

        <div className="mt-5 flex items-center gap-2">
          <div className="mono-tiny text-[#71717A]">Sticker color</div>
          <span
            className="inline-block w-5 h-5 rounded border border-[#E4E4E7]"
            style={{ backgroundColor: result.sticker_color || "#0F8F4F" }}
            data-testid="scan-sticker-color"
          />
          <span className="mono-tiny text-[#71717A]">{result.sticker_color}</span>
        </div>

        <div className="my-5 h-px bg-[#E4E4E7]" />

        <div className="mono-tiny text-[#71717A]">Vehicle</div>
        <div className="font-display text-2xl font-bold mt-1" data-testid="scan-vehicle">
          {v.year} {v.make} {v.model}
        </div>
        <div className="text-sm text-[#71717A]">{v.color}</div>
        <div className="mt-3 inline-block bg-[#09090B] text-white px-3 py-1.5 rounded font-mono text-sm" data-testid="scan-plate">
          {v.license_plate} · {v.state}
        </div>
        {v.permit_type && (
          <div className="mt-3">
            <span
              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold uppercase border ${permitClass(v.permit_type)}`}
              data-testid="scan-permit-type"
            >
              {v.permit_type} permit
            </span>
          </div>
        )}

        {/* Vehicle photo gallery — up to 4 thumbnails, click to expand */}
        {v.images && v.images.length > 0 && (
          <>
            <div className="my-5 h-px bg-[#E4E4E7]" />
            <div className="mono-tiny text-[#71717A] mb-2">Vehicle photos</div>
            <div
              className="grid grid-cols-3 sm:grid-cols-4 gap-2"
              data-testid="scan-vehicle-photos"
            >
              {v.images.slice(0, 4).map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(src)}
                  className="aspect-square rounded-md overflow-hidden border border-[#E4E4E7] bg-[#F4F4F5] hover:ring-2 hover:ring-[#0F8F4F] transition-all"
                  data-testid={`scan-vehicle-photo-${i}`}
                  aria-label={`View vehicle photo ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Vehicle photo ${i + 1}`}
                    width="200"
                    height="200"
                    loading="lazy"
                    decoding="async"
                    fetchpriority={i === 0 ? "high" : "low"}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
            <div className="mono-tiny text-[#71717A] mt-2">Tap a photo to enlarge</div>
          </>
        )}
        </motion.div>)}
      </div>

      <div className="text-center text-white/50 mt-8 mono-tiny">
        Verified via ScanMint · {new Date().toLocaleString()}
      </div>

      {/* Lightbox — fullscreen overlay for clicked vehicle photo */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(null)}
            data-testid="scan-lightbox"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(null);
              }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              aria-label="Close photo"
              data-testid="scan-lightbox-close"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
            <motion.img
              src={lightbox}
              alt="Vehicle"
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              className="max-h-[90vh] max-w-[95vw] object-contain rounded-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </ScanShell>
  );
}

// Color-code the permit badge so security/staff can spot the type at a glance.
function permitClass(type) {
  const t = (type || "").toLowerCase();
  if (t === "resident")
    return "bg-[#0F8F4F]/10 text-[#0F8F4F] border-[#0F8F4F]/30";
  if (t === "visitor" || t === "guest")
    return "bg-[#0EA5E9]/10 text-[#0369A1] border-[#0EA5E9]/30";
  if (t === "reserved")
    return "bg-[#A855F7]/10 text-[#7C3AED] border-[#A855F7]/30";
  if (t === "staff")
    return "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/30";
  // fallback
  return "bg-[#3F3F46]/10 text-[#3F3F46] border-[#3F3F46]/30";
}

function ScanShell({ color, children }) {
  return (
    <div
      className="min-h-screen text-white flex flex-col items-center px-6 py-12"
      style={{
        background: `linear-gradient(180deg, ${color}20 0%, #09090B 30%, #09090B 100%)`,
      }}
    >
      <div className="mb-10 md:mb-16">
        <img
          src="/scanmint-logo.png"
          alt="ScanMint"
          className="h-10 w-auto select-none"
          draggable={false}
        />
      </div>
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
